import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { custApi } from "../../lib/customerApi";
import { MePageTitle, MeSection, MeStagger } from "./MeUI";
import { BagRequestModal } from "./MeDashboard";
import { useCustomerCms } from "../../lib/useCustomerCms";
import {
  Recycle, ShoppingBag, Package, Truck, CheckCircle,
  PaperPlaneTilt, CaretRight, Storefront, ClipboardText, Clock,
} from "@phosphor-icons/react";

const COLOUR_HEX = {
  yellow: "#F5C518", blue: "#1E40AF", red: "#DC2626",
  white: "#F5F5F5", black: "#1F2937", green: "#15803D",
};

/**
 * /me/hygiene — Hygiene hub
 *   1. Waste Management SR (new / tracking / history)
 *   2. Biohazard Store
 *   3. Biohazard Bag Ordering (Request New Bags modal + orders list)
 */
export default function MeHygieneHub() {
  const [wm, setWm] = useState({ open_count: 0, done_count: 0 });
  const [bag, setBag] = useState(null);
  const [bagOrders, setBagOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBagModal, setShowBagModal] = useState(false);
  const cms = useCustomerCms();
  const v = (k) => cms.isFeatureVisible("hygiene", k);

  const loadBags = () => {
    custApi.get("/me/bags/summary").then(r => setBag(r.data)).catch(() => setBag({}));
    custApi.get("/me/bag-requests").then(r => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setBagOrders(rows.slice(0, 6));
    }).catch(() => setBagOrders([]));
  };

  useEffect(() => {
    Promise.all([
      custApi.get("/me/service-requests").then(r => r.data || []).catch(() => []),
    ]).then(([srs]) => {
      const open = srs.filter(s => !["completed", "cancelled", "closed", "done"].includes((s.status || "").toLowerCase())).length;
      const done = srs.length - open;
      setWm({ open_count: open, done_count: done });
      setLoading(false);
    });
    loadBags();
  }, []);

  return (
    <div className="font-body pb-6" data-testid="me-hygiene">
      <MePageTitle
        eyebrow="Hygiene"
        title={<>Clean home,<br/><span className="text-[#DC2626]">clean conscience</span></>}
        subtitle="Waste pickups, biohazard supplies and safe disposal — all in one place."
      />

      <MeStagger stagger={40}>
        {/* ── Section 1 · Waste Management ── */}
        <MeSection className="mt-2">
          <SectionHead icon={Recycle} title="Waste Management" note="Pickup service requests" />
          <div className="grid grid-cols-3 gap-2 mt-2">
            <StatPill label="Open" value={loading ? "—" : wm.open_count} tone="amber" testid="hyg-wm-open"/>
            <StatPill label="Done" value={loading ? "—" : wm.done_count} tone="green" testid="hyg-wm-done"/>
            <StatPill label="Total" value={loading ? "—" : (wm.open_count + wm.done_count)} tone="slate"/>
          </div>
          <div className="mt-3 space-y-2">
            {v("wm_new") && <HubLink to="/me/requests?action=new" icon={PaperPlaneTilt} title="New pickup request" desc="Schedule a home / clinic pickup" tone="red" testid="hyg-wm-new"/>}
            {v("wm_tracking") && <HubLink to="/me/tracking" icon={Truck} title="Tracking" desc="See live status of active pickups" tone="blue" testid="hyg-wm-tracking"/>}
            {v("wm_history") && <HubLink to="/me/requests" icon={ClipboardText} title="History" desc="All past requests & certificates" tone="slate" testid="hyg-wm-history"/>}
          </div>
        </MeSection>

        {/* ── Section 2 · Biohazard Store ── */}
        {(v("store_browse") || v("store_orders")) && (
        <MeSection className="mt-6">
          <SectionHead icon={Storefront} title="Biohazard Store" note="Certified supplies at your door" />
          <div className="mt-3 space-y-2">
            {v("store_browse") && <HubLink to="/me/services/biohazard" icon={Storefront} title="Browse store" desc="Sharps containers, PPE, spill kits" tone="red" testid="hyg-store-browse"/>}
            {v("store_orders") && <HubLink to="/me/orders?category=biohazard" icon={Package} title="My orders" desc="Track deliveries & re-order" tone="slate" testid="hyg-store-orders"/>}
          </div>
        </MeSection>
        )}

        {/* ── Section 3 · Biohazard Bag Ordering ── */}
        {(v("bag_new") || v("bag_kpi") || v("bag_history")) && (
        <MeSection className="mt-6">
          <SectionHead icon={ShoppingBag} title="Biohazard Bag Ordering" note="QR-coded bags · CPCB compliant" />

          {/* Balance/pending/delivered mini KPI */}
          {v("bag_kpi") && bag && (bag.pending_bags != null || bag.balance_bags != null || bag.delivered_bags != null) && (
            <div className="mt-2 rounded-xl border border-[#F4C7B9] bg-[#FEF7F6] p-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#7A2E18] font-bold">Balance</div>
                <div className="text-lg font-black text-[#111827]" data-testid="hyg-bag-balance">{bag.balance_bags ?? 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#7A2E18] font-bold">Pending</div>
                <div className="text-lg font-black text-[#DC2626]" data-testid="hyg-bag-pending">{bag.pending_bags ?? 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#7A2E18] font-bold">Delivered</div>
                <div className="text-lg font-black text-[#15803D]" data-testid="hyg-bag-delivered">{bag.delivered_bags ?? 0}</div>
              </div>
            </div>
          )}

          <div className="mt-3 space-y-2">
            {v("bag_new") && <button
              type="button"
              onClick={() => setShowBagModal(true)}
              data-testid="hyg-bag-new"
              className="w-full flex items-center gap-3 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_14px_rgba(0,0,0,0.03)] px-3 py-3 active:scale-[0.995] transition text-left"
            >
              <div className="w-11 h-11 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
                <PaperPlaneTilt size={20} weight="fill"/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-bold text-[#111827] leading-tight">Request new bags</div>
                <div className="text-[11px] text-gray-500 mt-0.5 truncate">Yellow / red / blue / white — admin approves</div>
              </div>
              <CaretRight size={14} className="text-gray-400 shrink-0"/>
            </button>}
          </div>

          {/* Bag order history / tracking inline */}
          {v("bag_history") && bagOrders.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold px-1 mb-2 flex items-center gap-2">
                <Clock size={11} weight="bold"/> Recent bag orders
              </div>
              <div className="space-y-2">
                {bagOrders.map(o => <BagOrderRow key={o.id} o={o} />)}
              </div>
            </div>
          )}
        </MeSection>
        )}

        {/* Compliance strip */}
        <MeSection className="mt-6">
          <div className="rounded-2xl bg-gradient-to-br from-[#FEF7F6] to-[#FDECEA] border border-[#F4C7B9] p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white shrink-0 flex items-center justify-center">
              <CheckCircle size={20} weight="fill" className="text-[#DC2626]"/>
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-[#111827]">CPCB-compliant handling</div>
              <div className="text-[11px] text-[#5C5A55] leading-snug mt-0.5">
                Every pickup is bar-coded, weighed and issued with a certificate of safe disposal.
              </div>
            </div>
          </div>
        </MeSection>
      </MeStagger>

      {showBagModal && (
        <BagRequestModal
          onClose={() => setShowBagModal(false)}
          onCreated={() => { setShowBagModal(false); loadBags(); }}
        />
      )}
    </div>
  );
}

// ── shared tiles ──────────────────────────────────────────────
const SectionHead = ({ icon: Icon, title, note }) => (
  <div className="flex items-center gap-2">
    <div className="w-9 h-9 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
      <Icon size={18} weight="fill"/>
    </div>
    <div className="min-w-0">
      <div className="font-display font-black text-[16px] text-[#111827] leading-none">{title}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{note}</div>
    </div>
  </div>
);

const StatPill = ({ label, value, tone, testid }) => {
  const tones = {
    amber: "bg-[#FFF8E6] text-[#7A5A11] border-[#F9E4B7]",
    green: "bg-[#EEF7EC] text-[#1A3317] border-[#C6D4C2]",
    slate: "bg-white text-[#111827] border-[#E5E2D9]",
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} px-2 py-2 text-center`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</div>
      <div className="text-lg font-black leading-tight">{value}</div>
    </div>
  );
};

const HubLink = ({ to, icon: Icon, title, desc, tone, testid }) => {
  const tones = {
    red: "bg-[#FEE2E2] text-[#DC2626]",
    blue: "bg-[#E0EBFF] text-[#1E3A8A]",
    slate: "bg-[#F3F4F6] text-[#111827]",
  };
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-3 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_14px_rgba(0,0,0,0.03)] px-3 py-3 active:scale-[0.995] transition"
    >
      <div className={`w-11 h-11 rounded-xl ${tones[tone] || tones.slate} flex items-center justify-center shrink-0`}>
        <Icon size={20} weight="fill"/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-bold text-[#111827] leading-tight">{title}</div>
        <div className="text-[11px] text-gray-500 mt-0.5 truncate">{desc}</div>
      </div>
      <CaretRight size={14} className="text-gray-400 shrink-0"/>
    </Link>
  );
};

const BagOrderRow = ({ o }) => {
  const dot = COLOUR_HEX[(o.colour_code || "").toLowerCase()] || "#DC2626";
  const statusStyle = {
    approved: "bg-[#EEF7EC] text-[#1A3317]",
    pending: "bg-[#FFF8E6] text-[#7A5A11]",
    delivered: "bg-[#EEF7EC] text-[#1A3317]",
    rejected: "bg-[#F4C7B9] text-[#7A2E18]",
    cancelled: "bg-[#F3F4F6] text-[#5C5A55]",
  }[(o.status || "").toLowerCase()] || "bg-[#F3F4F6] text-[#5C5A55]";
  const payStyle = {
    paid: "bg-[#EEF7EC] text-[#1A3317]",
    unpaid: "bg-[#F4C7B9] text-[#7A2E18]",
    cod: "bg-[#FFF8E6] text-[#7A5A11]",
  }[(o.payment_status || o.payment_mode || "").toLowerCase()] || "";
  return (
    <div
      className="flex items-center gap-3 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_14px_rgba(0,0,0,0.03)] px-3 py-3"
      data-testid={`hyg-bag-order-${o.id}`}
    >
      <span
        className="w-9 h-9 rounded-full shrink-0 border border-gray-200"
        style={{ background: dot }}
        title={o.colour_code}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[11px] text-[#DC2626]">{o.request_no || o.id.slice(0, 8)}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${statusStyle}`}>{o.status || "—"}</span>
          {payStyle && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${payStyle}`}>
              {(o.payment_status || o.payment_mode || "").toLowerCase()}
            </span>
          )}
        </div>
        <div className="text-[12px] font-bold text-[#111827] mt-0.5">
          {o.quantity} × {(o.colour_code || "").toUpperCase()} bags
          {o.total != null && <span className="ml-2 font-mono text-[#DC2626]">₹{Number(o.total).toFixed(0)}</span>}
        </div>
        <div className="text-[10px] text-gray-500 mt-0.5">
          {o.created_at ? new Date(o.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
        </div>
      </div>
    </div>
  );
};
